// Teksty o synchronizacji dla rodzica i małe znaczki dla dziecka (czyste funkcje — testowane bez przeglądarki).
// Znaczek dla dziecka idzie za przełącznikiem EN/PL (parametr `lang`, domyślnie polski); zdania dla rodzica zostają po polsku.
import type { TeacherLang } from '../types.ts';
import type { Pair } from './i18n.ts';
import type { SyncState } from './sync.ts';

const BADGE_LABEL = {
  synced: { en: 'Progress saved on the server', pl: 'Postęp zapisany na serwerze' },
  saving: { en: 'Saving progress…', pl: 'Zapisuję postęp…' },
  offline: { en: 'No internet — progress will be saved when it is back', pl: 'Brak internetu — postęp zapisze się, gdy wróci' },
  error: { en: 'The server did not accept the progress — I will try again', pl: 'Serwer nie przyjął postępu — spróbuję ponownie' },
  noAccess: { en: 'No access to the server', pl: 'Brak dostępu do serwera' },
  connecting: { en: 'Connecting to the server…', pl: 'Łączę z serwerem…' },
} as const satisfies Record<string, Pair>;

/**
 * Znaczek przy imieniu dziecka. Gdy wszystko jest zapisane (`quiet`), ekran go NIE pokazuje (chmurka przy awatarze przeszkadzała) — znaczek pojawia się
 * dopiero wtedy, gdy coś jest nie tak lub trwa zapis: 🔄 zapisuję, 📴 brak internetu, ⚠️ serwer nie przyjął postępu / brak dostępu.
 */
export function syncBadge(state: SyncState, lang: TeacherLang = 'pl'): { icon: string; label: string; quiet: boolean } {
  switch (state) {
    case 'synced':
      return { icon: '☁️', label: BADGE_LABEL.synced[lang], quiet: true };
    case 'syncing':
    case 'pending':
      return { icon: '🔄', label: BADGE_LABEL.saving[lang], quiet: false };
    case 'offline':
      return { icon: '📴', label: BADGE_LABEL.offline[lang], quiet: false };
    case 'error':
      return { icon: '⚠️', label: BADGE_LABEL.error[lang], quiet: false };
    case 'gone':
    case 'unpaired':
      return { icon: '⚠️', label: BADGE_LABEL.noAccess[lang], quiet: false };
    case 'idle':
      return { icon: '🔄', label: BADGE_LABEL.connecting[lang], quiet: false };
  }
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** „dziś 14:32”, „wczoraj 21:05” albo „12.10 08:15” (czas lokalny przeglądarki). */
export function formatSyncTime(iso: string | null, now: Date): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (day(d) === day(now)) return `dziś ${hm}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (day(d) === day(yesterday)) return `wczoraj ${hm}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${hm}`;
}

/** Jedno zdanie o stanie zapisu postępu dla rodzica (sekcja „Dla rodzica” — zostaje po polsku). */
export function syncLine(state: SyncState, detail: string, syncedAt: string | null, now: Date): string {
  const when = formatSyncTime(syncedAt, now);
  switch (state) {
    case 'synced':
      return `Postęp jest zapisany na serwerze${when ? ` (ostatnio ${when})` : ''}. Wyczyszczenie przeglądarki go nie kasuje.`;
    case 'syncing':
    case 'pending':
      return 'Zapisuję postęp na serwerze…';
    case 'offline':
      return 'Brak połączenia z serwerem. Postęp jest bezpieczny na tym urządzeniu i wyśle się sam, gdy wróci internet.';
    case 'error':
      return `Serwer nie przyjął postępu${detail ? ` (${detail})` : ''}. Postęp zostaje na tym urządzeniu i wyśle się przy kolejnej próbie.`;
    case 'gone':
      return 'Ten profil został usunięty przez rodzica.';
    case 'unpaired':
      return 'Serwer nie rozpoznaje tego urządzenia — trzeba zalogować je ponownie PIN-em rodzica.';
    case 'idle':
      return 'Synchronizacja jeszcze nie ruszyła.';
  }
}
